import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { 
  Key, 
  Plus, 
  Edit2, 
  Trash2, 
  Eye, 
  EyeOff,
  Globe,
  Lock,
  Copy,
  ExternalLink,
  Search
} from "lucide-react";

const CREDENTIAL_TYPES = [
  { value: "zomato", label: "Zomato Dashboard" },
  { value: "swiggy", label: "Swiggy Dashboard" },
  { value: "pos", label: "POS System" },
  { value: "bank", label: "Bank Portal" },
  { value: "accounting", label: "Accounting Software" },
  { value: "email", label: "Email Account" },
  { value: "social", label: "Social Media" },
  { value: "other", label: "Other" }
];

const CredentialsManager = ({ user }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    type: "other",
    url: "",
    username: "",
    password: "",
    notes: ""
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'superuser';

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/credentials");
      setCredentials(response.data);
    } catch (error) {
      toast.error("Failed to fetch credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.url) {
      toast.error("Name and URL are required");
      return;
    }

    try {
      if (editingCredential) {
        await axiosInstance.put(`/credentials/${editingCredential.id}`, formData);
        toast.success("Credential updated successfully");
      } else {
        await axiosInstance.post("/credentials", formData);
        toast.success("Credential added successfully");
      }
      
      fetchCredentials();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save credential");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this credential?")) return;
    
    try {
      await axiosInstance.delete(`/credentials/${id}`);
      toast.success("Credential deleted");
      fetchCredentials();
    } catch (error) {
      toast.error("Failed to delete credential");
    }
  };

  const handleEdit = (credential) => {
    setEditingCredential(credential);
    setFormData({
      name: credential.name,
      type: credential.type,
      url: credential.url,
      username: credential.username,
      password: credential.password,
      notes: credential.notes || ""
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingCredential(null);
    setFormData({
      name: "",
      type: "other",
      url: "",
      username: "",
      password: "",
      notes: ""
    });
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getTypeLabel = (type) => {
    const found = CREDENTIAL_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "zomato":
      case "swiggy":
        return "🍕";
      case "pos":
        return "💳";
      case "bank":
        return "🏦";
      case "accounting":
        return "📊";
      case "email":
        return "📧";
      case "social":
        return "📱";
      default:
        return "🔐";
    }
  };

  const filteredCredentials = credentials.filter(cred => 
    cred.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cred.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cred.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="credentials-manager">
      {/* Header */}
      <Card className="shadow-lg border-0 bg-gradient-to-br from-slate-50 to-gray-50">
        <CardHeader className="bg-gradient-to-r from-slate-700 to-gray-800 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Key className="w-6 h-6" />
                Credentials Manager
              </CardTitle>
              <CardDescription className="text-slate-200 mt-1">
                Securely store and manage login credentials
              </CardDescription>
            </div>
            {isAdmin && (
              <Button 
                onClick={() => setDialogOpen(true)}
                className="gap-2 bg-white text-slate-800 hover:bg-slate-100"
                data-testid="add-credential-btn"
              >
                <Plus className="w-4 h-4" />
                Add Credential
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search credentials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-credentials"
            />
          </div>
        </CardContent>
      </Card>

      {/* Credentials List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading credentials...</div>
      ) : filteredCredentials.length === 0 ? (
        <Card className="shadow-lg">
          <CardContent className="py-12 text-center text-gray-500">
            <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No Credentials Found</p>
            <p className="text-sm mt-1">
              {searchQuery ? "Try a different search term" : "Add your first credential to get started"}
            </p>
            {isAdmin && !searchQuery && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Credential
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-lg">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Password</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCredentials.map((cred) => (
                  <TableRow key={cred.id} data-testid={`credential-row-${cred.id}`}>
                    <TableCell className="text-2xl">
                      {getTypeIcon(cred.type)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {cred.name}
                      {cred.notes && (
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]">
                          {cred.notes}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        {getTypeLabel(cred.type)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <a 
                          href={cred.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1 text-sm truncate max-w-[200px]"
                        >
                          <Globe className="w-3 h-3" />
                          {cred.url.replace(/^https?:\/\//, '').split('/')[0]}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{cred.username}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(cred.username, "Username")}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">
                          {visiblePasswords[cred.id] ? cred.password : "••••••••"}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => togglePasswordVisibility(cred.id)}
                        >
                          {visiblePasswords[cred.id] ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(cred.password, "Password")}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(cred)}
                            data-testid={`edit-credential-${cred.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(cred.id)}
                            data-testid={`delete-credential-${cred.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="credential-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {editingCredential ? "Edit Credential" : "Add New Credential"}
            </DialogTitle>
            <DialogDescription>
              {editingCredential 
                ? "Update the credential details" 
                : "Enter the login credentials for secure storage"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Zomato Partner Dashboard"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="credential-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger data-testid="credential-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREDENTIAL_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://partner.zomato.com"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
                data-testid="credential-url-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="Enter username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  data-testid="credential-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="credential-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes or instructions"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="credential-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-credential-btn">
                {editingCredential ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CredentialsManager;
